import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Shield, Zap, ArrowRight, TrendingUp, Globe, 
  CheckCircle, Users, Building2, Package
} from 'lucide-react';

const PartnersPage = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="partners-page">
      {/* Navigation */}
      <nav className="glass sticky top-0 z-50 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-4" data-testid="logo-link">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/30 overflow-hidden">
                <img 
                  src="/assets/logo/sellesend-logo.png"
                  alt="SelleSend Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-3xl font-bold tracking-tight bg-gradient-to-r from-emerald-500 via-green-500 to-blue-500 bg-clip-text text-transparent">SelleSend</span>
                <span className="text-xs text-emerald-600 tracking-tight -mt-1">Global Payments • Shipping • Protection</span>
              </div>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50">Login</Button>
              </Link>
              <Link to="/register">
                <Button className="btn-press bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600">
                  Get Started
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,#0F172A_1px,transparent_1px)] bg-[length:32px_32px]" />
        </div>
        <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
          <p className="text-xs font-mono uppercase tracking-widest text-emerald-500 mb-6">
            OUR PARTNERSHIPS
          </p>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-semibold tracking-tight mb-8">
            Aligned by Purpose.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-blue-600">Connected by Innovation.</span>
          </h1>
          <p className="text-xl md:text-2xl text-slate-600 dark:text-slate-300 leading-relaxed max-w-3xl mx-auto">
            We believe in the strength of aligned partnerships. By bringing together our shared capabilities, we unlock smarter innovation, deliver more resilient payment solutions, and create meaningful advantages for our customers.
          </p>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-20 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-10 border border-slate-200 dark:border-slate-700 hover:shadow-xl transition-all hover:-translate-y-1">
              <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center mb-6">
                <Zap className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="font-semibold text-xl mb-3">Smarter Innovation</h3>
              <p className="text-slate-500 dark:text-slate-400 leading-relaxed">
                Through collaboration across our ecosystem, we build intelligent systems that reduce risk and expand opportunity.
              </p>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-10 border border-slate-200 dark:border-slate-700 hover:shadow-xl transition-all hover:-translate-y-1">
              <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-xl mb-3">Resilient Solutions</h3>
              <p className="text-slate-500 dark:text-slate-400 leading-relaxed">
                We don't just process transactions — we deliver payment solutions built for trust, security, and performance.
              </p>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-10 border border-slate-200 dark:border-slate-700 hover:shadow-xl transition-all hover:-translate-y-1">
              <div className="w-14 h-14 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center mb-6">
                <TrendingUp className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="font-semibold text-xl mb-3">Elevated Performance</h3>
              <p className="text-slate-500 dark:text-slate-400 leading-relaxed">
                We create meaningful advantages for our customers by connecting capabilities that elevate performance across the board.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-white dark:bg-slate-950">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-lg md:text-xl text-slate-600 dark:text-slate-300 leading-relaxed mb-12">
            Through collaboration across our ecosystem, we don't just process transactions — we build intelligent systems that reduce risk, expand opportunity, and elevate performance.
          </p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-6 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
              <Globe className="w-8 h-8 text-emerald-500 mx-auto mb-3" />
              <p className="font-semibold">Global Reach</p>
              <p className="text-sm text-slate-500 mt-1">100+ Countries</p>
            </div>
            <div className="p-6 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
              <Package className="w-8 h-8 text-blue-500 mx-auto mb-3" />
              <p className="font-semibold">Carrier Network</p>
              <p className="text-sm text-slate-500 mt-1">300+ Partners</p>
            </div>
            <div className="p-6 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
              <Users className="w-8 h-8 text-purple-500 mx-auto mb-3" />
              <p className="font-semibold">Ecosystem</p>
              <p className="text-sm text-slate-500 mt-1">Multi-Platform</p>
            </div>
            <div className="p-6 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
              <Building2 className="w-8 h-8 text-amber-500 mx-auto mb-3" />
              <p className="font-semibold">Enterprise</p>
              <p className="text-sm text-slate-500 mt-1">Licensed & Secure</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Quote */}
      <section className="py-24 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-white/90 text-2xl md:text-3xl leading-relaxed font-light italic mb-10">
            "Join us in shaping a connected, forward-thinking network where innovation, trust, and growth define the future of payments."
          </p>
          <Link to="/register">
            <Button size="lg" className="btn-press h-14 px-10 text-lg bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600" data-testid="partners-cta-btn">
              Become a Partner
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="text-slate-500 text-sm">SelleSend © 2024 — Global Payments • Shipping • Protection</span>
          <Link to="/" className="text-sm text-emerald-600 hover:text-emerald-700">Back to Home</Link>
        </div>
      </footer>
    </div>
  );
};

export default PartnersPage;
